import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Star as StarIcon, Lock as LockIcon } from '@mui/icons-material';
import cfosService from '../../services/cfosService';
import FileTypeIcon from './FileTypeIcon';
import { isOfficeWebEditableSize, isOfficeWebFile, isPdfFile } from '../../lib/cfos/fileKind';
import type { FileRecord, SearchFilters, User } from '../../lib/cfos/types';

export interface SearchTabProps {
  user: User;
  sandboxId?: string | null;
  onOpenViewer: (file: FileRecord) => void;
  onOpenOffice: (file: FileRecord, opts?: { preview?: boolean; edit?: boolean }) => void;
  notify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  panelBorder: string;
}

function formatSize(value: number) {
  return value < 1024 * 1024
    ? `${Math.max(1, Math.round(value / 1024))} KB`
    : `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function formatPerson(user?: { name: string; last_name: string } | null) {
  if (!user) return '—';
  return `${user.name} ${user.last_name}`.trim() || '—';
}

async function downloadFileRecord(file: FileRecord) {
  const blob = await cfosService.fileContentBlob(file.id);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

const SearchTab: React.FC<SearchTabProps> = ({
  onOpenViewer,
  onOpenOffice,
  notify,
  panelBorder,
  sandboxId = null,
}) => {
  const [filters, setFilters] = useState<SearchFilters>({});
  const [results, setResults] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  const search = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      setResults(await cfosService.search({ ...filters, sandboxId: sandboxId || undefined }));
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const openResult = (file: FileRecord) => {
    if (isPdfFile(file)) {
      onOpenViewer(file);
      return;
    }
    if (isOfficeWebFile(file) && isOfficeWebEditableSize(file)) {
      onOpenOffice(file);
      return;
    }
    if (isOfficeWebFile(file) && !isOfficeWebEditableSize(file)) {
      notify('File exceeds Microsoft 365 web edit size limit — downloading instead', 'warning');
    }
    void downloadFileRecord(file).catch((reason) => notify((reason as Error).message, 'error'));
  };

  const setField = (key: keyof SearchFilters, value: string) => {
    setFilters((current) => ({ ...current, [key]: value || undefined }));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box component="form" onSubmit={(e) => void search(e)} sx={{ border: 1, borderColor: panelBorder, borderRadius: 1, p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Search files</Typography>
        <Stack spacing={2}>
          <TextField size="small" label="Keywords" value={filters.q || ''} onChange={(e) => setField('q', e.target.value)} fullWidth />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" label="Tag" value={filters.tag || ''} onChange={(e) => setField('tag', e.target.value)} fullWidth />
            <TextField size="small" label="Path" value={filters.path || ''} onChange={(e) => setField('path', e.target.value)} fullWidth />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField size="small" label="Uploader" value={filters.uploader || ''} onChange={(e) => setField('uploader', e.target.value)} fullWidth />
            <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={filters.date_from || ''} onChange={(e) => setField('date_from', e.target.value)} fullWidth />
            <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={filters.date_to || ''} onChange={(e) => setField('date_to', e.target.value)} fullWidth />
          </Stack>
          <Box>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Searching…' : 'Search'}
            </Button>
          </Box>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>}

      {!loading && searched && (
        <Box sx={{ border: 1, borderColor: panelBorder, borderRadius: 1, overflow: 'hidden' }}>
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5, borderBottom: 1, borderColor: panelBorder }}>
            {results.length} result{results.length === 1 ? '' : 's'}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Paths</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Last modification</TableCell>
                <TableCell>Uploaded</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((file) => (
                <TableRow
                  key={file.id}
                  hover
                  onDoubleClick={() => openResult(file)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FileTypeIcon filename={file.filename} />
                      <Typography variant="body2" fontWeight={600} noWrap>{file.filename}</Typography>
                      {file.is_contractual && <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                      {file.office_session && (
                        <LockIcon sx={{ fontSize: 16, color: 'warning.main' }} titleAccess="Microsoft 365 session" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" noWrap>
                      {file.paths.map((p) => (
                        p.sandbox_name ? `${p.sandbox_name}:${p.path}` : p.path
                      )).join(', ') || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>{formatPerson(file.uploader)}</Typography>
                  </TableCell>
                  <TableCell>{formatSize(file.file_size)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatPerson(file.last_modified_by || file.uploader)}
                    </Typography>
                  </TableCell>
                  <TableCell>{new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(file.created_at))}</TableCell>
                </TableRow>
              ))}
              {!results.length && (
                <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No files matched your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default SearchTab;
